import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { DownloadIcon, TrendingUpIcon, UsersIcon, BriefcaseIcon, DollarSignIcon } from 'lucide-react';
const placementData = [{
  month: 'Jan',
  placements: 45
}, {
  month: 'Feb',
  placements: 52
}, {
  month: 'Mar',
  placements: 68
}, {
  month: 'Apr',
  placements: 85
}, {
  month: 'May',
  placements: 102
}, {
  month: 'Jun',
  placements: 125
}, {
  month: 'Jul',
  placements: 140
}, {
  month: 'Aug',
  placements: 165
}, {
  month: 'Sep',
  placements: 190
}, {
  month: 'Oct',
  placements: 215
}, {
  month: 'Nov',
  placements: 240
}, {
  month: 'Dec',
  placements: 280
}];
const skillsData = [{
  name: 'Cloud Computing',
  learners: 450
}, {
  name: 'Cybersecurity',
  learners: 380
}, {
  name: 'Data Analytics',
  learners: 340
}, {
  name: 'Agentic AI',
  learners: 320
}, {
  name: 'Sales & RevOps',
  learners: 280
}, {
  name: 'UX/UI Design',
  learners: 210
}, {
  name: 'Project Mgmt',
  learners: 190
}, {
  name: 'Digital Marketing',
  learners: 160
}];
const sectorData = [{
  name: 'Technology',
  value: 45
}, {
  name: 'Healthcare',
  value: 25
}, {
  name: 'Finance',
  value: 15
}, {
  name: 'Manufacturing',
  value: 10
}, {
  name: 'Other',
  value: 5
}];
const salaryGrowthData = [{
  month: 'Month 0',
  salary: 28000
}, {
  month: 'Month 6',
  salary: 38000
}, {
  month: 'Month 12',
  salary: 52000
}, {
  month: 'Month 18',
  salary: 62000
}, {
  month: 'Month 24',
  salary: 72000
}];
const COLORS = ['#1f4a50', '#0ea5e9', '#10b981', '#f59e0b', '#64748b'];
export function ImpactDashboard() {
  return <div className="space-y-6 pb-8" data-id="element-221">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-222">
        <div data-id="element-223">
          <h1 className="text-2xl font-bold text-surface-900 tracking-tight" data-id="element-224">
            Impact & Analytics
          </h1>
          <p className="text-surface-500 text-sm mt-1" data-id="element-225">
            Platform-wide metrics for partners and stakeholders.
          </p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 bg-white border border-surface-200 text-surface-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-surface-50 transition-colors shadow-sm" data-id="element-226">
          <DownloadIcon className="w-4 h-4" data-id="element-227" />
          Export Report
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-228">
        {[{
        label: 'Total Placements',
        value: '2,450',
        icon: BriefcaseIcon,
        trend: '+15% vs last quarter',
        color: 'text-primary-600',
        bg: 'bg-primary-50'
      }, {
        label: 'Avg Salary Increase',
        value: '$28,500',
        icon: DollarSignIcon,
        trend: '+12% vs last year',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50'
      }, {
        label: 'Retention Rate (1yr)',
        value: '94%',
        icon: TrendingUpIcon,
        trend: 'Industry avg: 72%',
        color: 'text-primary-700',
        bg: 'bg-primary-100'
      }, {
        label: 'Active Learners',
        value: '8,200',
        icon: UsersIcon,
        trend: '+450 this month',
        color: 'text-primary-800',
        bg: 'bg-primary-200'
      }].map((stat, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-white p-5 rounded-xl border border-surface-200 shadow-sm" data-id="element-229">
            <div className="flex items-center gap-3 mb-3" data-id="element-230">
              <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`} data-id="element-231">
                <stat.icon className={`w-5 h-5 ${stat.color}`} data-id="element-232" />
              </div>
              <h3 className="text-sm font-medium text-surface-600" data-id="element-233">
                {stat.label}
              </h3>
            </div>
            <div data-id="element-234">
              <p className="text-2xl font-bold text-surface-900 tracking-tight" data-id="element-235">
                {stat.value}
              </p>
              <p className="text-xs text-surface-500 mt-1" data-id="element-236">{stat.trend}</p>
            </div>
          </motion.div>)}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-237">
        {/* Placements Trend */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-238">
          <h2 className="text-lg font-semibold text-surface-900 mb-6" data-id="element-239">
            Job Placements (YTD)
          </h2>
          <div className="h-72" data-id="element-240">
            <ResponsiveContainer width="100%" height="100%" data-id="element-241">
              <LineChart data={placementData} margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 0
            }} data-id="element-242">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-243" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} dy={10} data-id="element-244" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-245" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} cursor={{
                stroke: '#cbd5e1',
                strokeWidth: 1,
                strokeDasharray: '3 3'
              }} data-id="element-246" />
                <Line type="monotone" dataKey="placements" stroke="#1f4a50" strokeWidth={3} dot={{
                r: 4,
                strokeWidth: 2
              }} activeDot={{
                r: 6
              }} data-id="element-247" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Salary Growth */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.45
      }} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-248">
          <h2 className="text-lg font-semibold text-surface-900 mb-6" data-id="element-249">
            Average Salary Growth
          </h2>
          <div className="h-72" data-id="element-250">
            <ResponsiveContainer width="100%" height="100%" data-id="element-251">
              <AreaChart data={salaryGrowthData} margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 10
            }} data-id="element-252">
                <defs data-id="element-253">
                  <linearGradient id="colorSalary" x1="0" y1="0" x2="0" y2="1" data-id="element-254">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-255" />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-256" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-257" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} dy={10} data-id="element-258" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} tickFormatter={value => `$${value / 1000}k`} data-id="element-259" />
                <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Salary']} contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-260" />
                <Area type="monotone" dataKey="salary" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorSalary)" data-id="element-261" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Skills Distribution */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-262">
          <h2 className="text-lg font-semibold text-surface-900 mb-6" data-id="element-263">
            Learners by Track
          </h2>
          <div className="h-80" data-id="element-264">
            <ResponsiveContainer width="100%" height="100%" data-id="element-265">
              <BarChart data={skillsData} layout="vertical" margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 40
            }} data-id="element-266">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-267" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-268" />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                fill: '#475569',
                fontSize: 12,
                fontWeight: 500
              }} dx={-10} data-id="element-269" />
                <Tooltip cursor={{
                fill: '#f1f5f9'
              }} contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-270" />
                <Bar dataKey="learners" fill="#1f4a50" radius={[0, 4, 4, 0]} barSize={20} data-id="element-271" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Employment Sectors */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-272">
          <h2 className="text-lg font-semibold text-surface-900 mb-2" data-id="element-273">
            Employment by Sector
          </h2>
          <p className="text-sm text-surface-500 mb-6" data-id="element-274">
            Distribution of job placements across industries.
          </p>
          <div className="h-72" data-id="element-275">
            <ResponsiveContainer width="100%" height="100%" data-id="element-276">
              <PieChart data-id="element-277">
                <Pie data={sectorData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={5} dataKey="value" data-id="element-278">
                  {sectorData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-279" />)}
                </Pie>
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-280" />
                <Legend verticalAlign="bottom" height={36} iconType="circle" data-id="element-281" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Success Stories */}
      <div className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-282">
        <h3 className="text-lg font-semibold text-surface-900 mb-6" data-id="element-283">
          Recent Success Stories
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-284">
          {[{
          name: 'David M.',
          role: 'Cloud Engineer',
          company: 'Accenture',
          salary: '$78K',
          increase: '+$42K',
          quote: 'Completed AWS track in 8 months'
        }, {
          name: 'Sarah J.',
          role: 'Data Analyst',
          company: 'Tableau',
          salary: '$65K',
          increase: '+$32K',
          quote: 'First in family to work in tech'
        }, {
          name: 'Michael T.',
          role: 'SOC Analyst',
          company: 'CrowdStrike',
          salary: '$72K',
          increase: '+$38K',
          quote: 'Earned CompTIA Security+ in 6 months'
        }, {
          name: 'Keisha R.',
          role: 'Salesforce Admin',
          company: 'HubSpot',
          salary: '$68K',
          increase: '+$35K',
          quote: 'Transitioned from retail to tech'
        }].map((story, i) => <div key={i} className="p-5 rounded-xl bg-surface-50 border border-surface-100" data-id="element-285">
              <div className="flex items-center gap-3 mb-4" data-id="element-286">
                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold" data-id="element-287">
                  {story.name.charAt(0)}
                </div>
                <div data-id="element-288">
                  <p className="text-sm font-semibold text-surface-900" data-id="element-289">
                    {story.name}
                  </p>
                  <p className="text-xs text-surface-500" data-id="element-290">
                    {story.role} at {story.company}
                  </p>
                </div>
              </div>
              <div className="mb-4" data-id="element-291">
                <span className="text-xs font-medium text-surface-500 block mb-1" data-id="element-292">
                  Current Salary
                </span>
                <div className="flex items-baseline gap-2" data-id="element-293">
                  <span className="text-lg font-bold text-surface-900" data-id="element-294">
                    {story.salary}
                  </span>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-295">
                    {story.increase}
                  </span>
                </div>
              </div>
              <p className="text-sm text-surface-600 italic" data-id="element-296">"{story.quote}"</p>
            </div>)}
        </div>
      </div>

      {/* Partner Logos */}
      <div className="pt-8 pb-4" data-id="element-297">
        <p className="text-center text-sm font-medium text-surface-500 mb-6 uppercase tracking-wider" data-id="element-298">
          Trusted by 150+ Employer Partners
        </p>
        <div className="flex flex-wrap justify-center gap-8 md:gap-12 opacity-60 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-299">
          {['Accenture', 'Amazon Web Services', 'Anthropic', 'CrowdStrike', 'Deloitte', 'HubSpot', 'Salesforce', 'Snowflake'].map((partner, i) => <div key={i} className="text-lg font-bold text-surface-900" data-id="element-300">
              {partner}
            </div>)}
        </div>
      </div>
    </div>;
}